import {
    Directive, Input, HostBinding,
    inject,
    DestroyRef,
    OnInit,
    ChangeDetectorRef
} from "@angular/core";
import { FormListComponent } from "../../shared/form-list/form-list.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";

/**
 * Structural directive that provides context for form list items.
 * Used within FormListComponent to define templates for different item types.
 *
 * Supports three types of items:
 * - "item": Represents an existing item in the list with associated data
 * - "add": Represents an add button or placeholder for creating new items
 * - "requiredText": Represents the required text shown when there are no items in the list. This is optional.
 *
 * @example
 * ```html
 * <!-- Template for existing items -->
 * <ng-container *mgdFormListItem="let item; type: 'item'">
 *   <span>{{ item.name }}</span>
 *   <mgd-form-list-item-controls></mgd-form-list-item-controls>
 * </ng-container>
 *
 * <!-- Template for add button -->
 * <ng-container *mgdFormListItem="type: 'add'">
 *   <span>Add New Item</span>
 *   <mgd-form-list-item-controls></mgd-form-list-item-controls>
 * </ng-container>
 *
 * <!-- Template for Required Text with Add button -->
 * <ng-container *mgdFormListItem="type: 'requiredText'">
 *   <span>A new item is required</span>
 *   <mgd-form-list-item-controls></mgd-form-list-item-controls>
 * </ng-container>
 *
 * ```
 *
 * @example
 * ```html
 * <!-- Using with explicit data binding -->
 * <div *mgdFormListItem="let itemData; type: 'item'; data: myItem">
 *   {{ itemData.title }}
 * </div>
 * ```
 */

@Directive({
    selector: "[mgdFormListItem]"
})
export class FormListItemDirective implements OnInit {

    /**
     * The data associated with this form list item.
     * Only relevant for "item" type items. Should be undefined for "add" type items.
     *
     * @example
     * ```html
     * <div *mgdFormListItem="let item; type: 'item'; data: myItemData">
     * ```
     */

    /**
     * The type of form list item this directive represents.
     *
     * - "item": An existing item in the list that can be edited or removed
     * - "add": A placeholder or button for adding new items to the list
     * - "requiredText": Text shown when no items exist, prompting the user to add one
     *
     * @example
     * ```html
     * <!-- For existing items -->
     * <div *mgdFormListItem="let item; type: 'item'">
     *
     * <!-- For add functionality -->
     * <div *mgdFormListItem="type: 'add'">
     *
     * <!-- For required text with add functionality  -->
     * <div *mgdFormListItem="type: 'requiredText'">
     *
     * ```
     */
    @Input("mgdFormListItem") type: "add" | "item" | "requiredText";

    @HostBinding("class.active") active = false;

    /** Injected FormListComponent from the host element to perform list operations */
    private readonly _formList = inject(FormListComponent, { host: true });
    private readonly _destroyRef = inject(DestroyRef);
    private readonly _cdRef = inject(ChangeDetectorRef);

    ngOnInit(): void {

        if (this._formList.subform) {
            this._formList.subform.closed.pipe(
                takeUntilDestroyed(this._destroyRef)
            ).subscribe(() => {
                this.active = false;
                this._cdRef.markForCheck();
            });
        }
    }

}
